import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Save, Loader2, Building2, MapPin, Search } from 'lucide-react'
import { z } from 'zod'
import { Controller } from 'react-hook-form'
import { PageHeader } from '@/components/ui/PageHeader'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Button } from '@/components/ui/Button'
import { StatusToggle } from '@/components/ui/StatusToggle'
import { FormSidemenu, type FormSection } from '@/components/ui/FormSidemenu'
import { useZodForm } from '@/hooks/useZodForm'
import { applyApiErrorsToForm } from '@/lib/api-errors'
import { onValidationFail } from '@/lib/form-helpers'
import { api } from '@/services/api/AxiosBaseService'
import { createCrudService } from '@/services/api/createCrudService'
import { LookupService, type State, type City } from '@/services/modules/LookupService'
import { SalesLinkService } from '@/services/modules/SalesLinkService'
import { onlyDigits, maskPhoneInput } from '@/lib/format'
import { toast } from '@/components/ui/Toast'

interface Branch {
  id: string
  companyId: string
  systemName: string
  corporateName: string | null
  cpfCnpj: string | null
  type: string
  email: string | null
  phone: string | null
  mobile: string | null
  status: 'ATIVO' | 'INATIVO'
  address?: {
    postalCode: string | null
    address: string | null
    number: string | null
    complement: string | null
    district: string | null
    city?: { id: string; city: string; state?: { id: string; abbreviation: string } | null } | null
  }
}

const BranchesService = createCrudService<Branch>('/my-company/branches', { searchField: 'system_name' })

const PHONE_RE = /^\d{10,11}$/
const maskCep = (v: string) => onlyDigits(v).slice(0, 8).replace(/^(\d{5})(\d)/, '$1-$2')

const schema = z.object({
  systemName:    z.string().trim().min(1, 'Informe o nome da filial.').max(200),
  corporateName: z.string().trim().max(200).or(z.literal('')).nullable(),
  cpfCnpj:       z.string().trim().max(18).or(z.literal('')).nullable(),
  type:          z.enum(['PESSOA FÍSICA', 'PESSOA JURÍDICA']),
  status:        z.enum(['ATIVO', 'INATIVO']),
  email:  z.string().trim().email('E-mail inválido.').or(z.literal('')).nullable(),
  phone:  z.string().trim().regex(PHONE_RE, 'Telefone deve ter 10 ou 11 dígitos.').or(z.literal('')).nullable(),
  mobile: z.string().trim().regex(PHONE_RE, 'Celular deve ter 10 ou 11 dígitos.').or(z.literal('')).nullable(),
  postalCode: z.string().trim().or(z.literal('')).nullable(),
  address:    z.string().trim().max(200).or(z.literal('')).nullable(),
  number:     z.string().trim().max(20).or(z.literal('')).nullable(),
  complement: z.string().trim().max(100).or(z.literal('')).nullable(),
  district:   z.string().trim().max(100).or(z.literal('')).nullable(),
  stateId:    z.string().nullable(),
  cityId:     z.string().nullable(),
})
type FormValues = z.infer<typeof schema>

export function BranchFormPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id?: string }>()
  const isEdit = !!id
  const [loading, setLoading] = useState(isEdit)
  const [activeSection, setActiveSection] = useState('basic')
  const [states, setStates] = useState<State[]>([])
  const [cities, setCities] = useState<City[]>([])
  const [cepLoading, setCepLoading] = useState(false)

  const form = useZodForm(schema, {
    defaultValues: {
      systemName: '', corporateName: null, cpfCnpj: null,
      type: 'PESSOA JURÍDICA', status: 'ATIVO',
      email: null, phone: null, mobile: null,
      postalCode: null, address: null, number: null, complement: null, district: null,
      stateId: null, cityId: null,
    },
  })
  const stateId = form.watch('stateId')

  useEffect(() => { LookupService.states().then(setStates).catch(() => setStates([])) }, [])
  useEffect(() => {
    if (!stateId) { setCities([]); return }
    LookupService.cities(stateId).then(setCities).catch(() => setCities([]))
  }, [stateId])

  useEffect(() => {
    if (!isEdit || !id) return
    setLoading(true)
    api.get<{ data: Branch }>(`/my-company/branches/${id}`)
      .then(r => {
        const b = r.data.data
        form.reset({
          systemName: b.systemName, corporateName: b.corporateName, cpfCnpj: b.cpfCnpj,
          type: (b.type as FormValues['type']) ?? 'PESSOA JURÍDICA', status: b.status,
          email: b.email, phone: b.phone ? maskPhoneInput(b.phone) : null, mobile: b.mobile ? maskPhoneInput(b.mobile) : null,
          postalCode: b.address?.postalCode ? maskCep(b.address.postalCode) : null,
          address: b.address?.address ?? null, number: b.address?.number ?? null,
          complement: b.address?.complement ?? null, district: b.address?.district ?? null,
          stateId: b.address?.city?.state?.id ?? null, cityId: b.address?.city?.id ?? null,
        })
      })
      .catch(() => toast.error({ title: 'Não encontrada', description: 'Filial não localizada.' }))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id])

  async function lookupCep() {
    const cep = onlyDigits(form.getValues('postalCode') ?? '')
    if (cep.length !== 8) { toast.error({ title: 'CEP inválido', description: 'O CEP precisa ter 8 dígitos.' }); return }
    setCepLoading(true)
    try {
      const r = await SalesLinkService.lookupCep(cep)
      if (!r) { toast.error('CEP não encontrado.'); return }
      const st = states.find(s => s.abbreviation === r.stateAbbr)
      form.setValue('address', r.address ?? '', { shouldValidate: false })
      form.setValue('district', r.district ?? '', { shouldValidate: false })
      if (st) {
        form.setValue('stateId', st.id, { shouldValidate: false })
        let cs = await LookupService.cities(st.id)
        let ci = cs.find(c => c.city.toLowerCase() === r.cityName.toLowerCase())
        if (!ci) { try { ci = await LookupService.findOrCreateCity(r.cityName, st.abbreviation); cs = [...cs, ci] } catch { /* ignore */ } }
        setCities(cs)
        if (ci) form.setValue('cityId', ci.id, { shouldValidate: false })
      }
    } catch { toast.error('Não foi possível buscar o CEP.') }
    finally { setCepLoading(false) }
  }

  async function onSubmit(values: FormValues) {
    try {
      const payload: Record<string, unknown> = {
        system_name:    values.systemName,
        corporate_name: values.corporateName || null,
        cpf_cnpj:       values.cpfCnpj ? onlyDigits(values.cpfCnpj) : null,
        type:           values.type,
        status:         values.status,
        email:          values.email || null,
        phone:          values.phone ? onlyDigits(values.phone) : null,
        mobile:         values.mobile ? onlyDigits(values.mobile) : null,
        postal_code:    values.postalCode ? onlyDigits(values.postalCode) : null,
        address:        values.address || null,
        number:         values.number || null,
        complement:     values.complement || null,
        district:       values.district || null,
        city_id:        values.cityId || null,
      }
      if (isEdit && id) await BranchesService.update(id, payload as Partial<Branch>)
      else              await BranchesService.create(payload as Partial<Branch>)

      toast.success({
        title: isEdit ? 'Filial atualizada' : 'Filial criada',
        description: isEdit ? 'As alterações foram salvas.' : 'A nova filial foi cadastrada.',
      })
      navigate('/my-company/branches')
    } catch (e) {
      applyApiErrorsToForm(e, form.setError, {
        system_name: 'systemName', corporate_name: 'corporateName', cpf_cnpj: 'cpfCnpj',
        postal_code: 'postalCode', city_id: 'cityId',
      })
    }
  }

  const errs = form.formState.errors
  const basicErr   = (errs.systemName ? 1 : 0) + (errs.corporateName ? 1 : 0) + (errs.cpfCnpj ? 1 : 0) + (errs.type ? 1 : 0) + (errs.email ? 1 : 0) + (errs.phone ? 1 : 0) + (errs.mobile ? 1 : 0)
  const locationErr = (errs.postalCode ? 1 : 0) + (errs.address ? 1 : 0) + (errs.number ? 1 : 0) + (errs.district ? 1 : 0)

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 size={28} className="animate-spin text-gray-400" /></div>
  }

  const basicSection = (
    <SectionShell title="Dados" description="Identificação e contato da filial.">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input label="Nome da filial" required {...form.register('systemName')} error={errs.systemName?.message} placeholder="Ex.: Matriz, Filial SP" />
        <Input label="Razão social" {...form.register('corporateName')} error={errs.corporateName?.message} placeholder="Razão social" />
        <Controller name="type" control={form.control} render={({ field, fieldState }) => (
          <Select label="Tipo de pessoa" required value={field.value} onChange={(e) => field.onChange(e.target.value)}
            options={[{ value: 'PESSOA FÍSICA', label: 'Pessoa Física' }, { value: 'PESSOA JURÍDICA', label: 'Pessoa Jurídica' }]}
            searchable={false} clearable={false} error={fieldState.error?.message} />
        )} />
        <Input label="CPF / CNPJ" {...form.register('cpfCnpj')} error={errs.cpfCnpj?.message} placeholder="Apenas dígitos" />
        <Input label="E-mail" type="email" {...form.register('email')} error={errs.email?.message} placeholder="filial@empresa.com.br" />
        <Input label="Telefone" {...form.register('phone')} error={errs.phone?.message} placeholder="(11) 3333-4444" />
        <Input label="Celular / WhatsApp" {...form.register('mobile')} error={errs.mobile?.message} placeholder="(11) 99999-9999" />
        <Controller name="status" control={form.control} render={({ field }) => (
          <div className="md:col-span-2 border-t border-gray-100 pt-4"><StatusToggle value={field.value} onChange={field.onChange} /></div>
        )} />
      </div>
    </SectionShell>
  )

  const locationSection = (
    <SectionShell title="Localização" description="Digite o CEP e clique na lupa para preencher automaticamente.">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Controller name="postalCode" control={form.control} render={({ field, fieldState }) => (
          <Input label="CEP" value={field.value ?? ''} onChange={(e) => field.onChange(maskCep(e.target.value))} placeholder="00000-000"
            error={fieldState.error?.message}
            actionRight={<button type="button" onClick={lookupCep} disabled={cepLoading} aria-label="Buscar CEP"
              className="cursor-pointer inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-500 hover:text-brand-600 hover:bg-gray-100 disabled:opacity-50">
              {cepLoading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}</button>} />
        )} />
        <Input label="Logradouro" wrapperClassName="md:col-span-2" {...form.register('address')} error={errs.address?.message} placeholder="Rua, avenida" />
        <Input label="Número" {...form.register('number')} error={errs.number?.message} placeholder="123" />
        <Input label="Complemento" {...form.register('complement')} placeholder="Sala, bloco" />
        <Input label="Bairro" {...form.register('district')} error={errs.district?.message} placeholder="Bairro" />
        <Controller name="stateId" control={form.control} render={({ field }) => (
          <Select label="Estado" value={field.value ?? ''} onChange={(e) => { field.onChange(e.target.value || null); form.setValue('cityId', null) }}
            placeholder="UF" options={states.map(s => ({ value: s.id, label: s.abbreviation }))} />
        )} />
        <Controller name="cityId" control={form.control} render={({ field }) => (
          <Select label="Cidade" wrapperClassName="md:col-span-2" value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value || null)}
            placeholder={stateId ? 'Selecione' : 'Escolha o estado primeiro'} disabled={!stateId}
            options={cities.map(c => ({ value: c.id, label: c.city }))} />
        )} />
      </div>
    </SectionShell>
  )

  const sections: FormSection[] = [
    { key: 'basic',    label: 'Dados',       icon: Building2, content: basicSection,    hasError: basicErr > 0,    errorCount: basicErr },
    { key: 'location', label: 'Localização', icon: MapPin,    content: locationSection, hasError: locationErr > 0, errorCount: locationErr },
  ]

  return (
    <form onSubmit={form.handleSubmit(onSubmit, (e) => {
      if (basicErr > 0) setActiveSection('basic')
      else if (locationErr > 0) setActiveSection('location')
      onValidationFail(e)
    })}>
      <PageHeader
        title={isEdit ? 'Editar filial' : 'Nova filial'}
        subtitle="Filiais da minha empresa"
        crumbs={[{ label: 'Minha empresa' }, { label: 'Filiais', to: '/my-company/branches' }, { label: isEdit ? 'Editar' : 'Novo' }]}
        backTo="/my-company/branches"
        actions={<Button type="submit" loading={form.formState.isSubmitting} iconLeft={<Save size={14} />}>Salvar</Button>}
      />
      <FormSidemenu sections={sections} activeKey={activeSection} onChange={setActiveSection} />
    </form>
  )
}

function SectionShell({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-md ring-1 ring-gray-200 p-5 sm:p-6">
      <div className="mb-4">
        <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
      {children}
    </div>
  )
}
